import logoImg from '@app/assets/logo.png'
import { initials, updateAttendanceMutationKey } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  Anchor,
  AppShell,
  Avatar,
  Burger,
  Container,
  createStyles,
  Drawer,
  Group,
  Header,
  Menu,
  NavLink,
  rem,
  Select,
  Title
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCashBanknote, IconLogout, IconUserEdit } from '@tabler/icons-react'
import { useIsMutating } from '@tanstack/react-query'
import { map, some } from 'lodash'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { signOut } from 'next-auth/react'
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react'

import { AppSelector } from './AppSelector'

const headerHeight = rem(80)
const useStyles = createStyles((theme) => ({
  burger: {
    [theme.fn.largerThan('md')]: {
      display: 'none'
    }
  },
  buttonLabel: {
    fontWeight: 'normal'
  },
  drawer: {
    [theme.fn.largerThan('md')]: {
      display: 'none'
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%'
  },
  links: {
    [theme.fn.smallerThan('md')]: {
      display: 'none'
    }
  },
  menu: {
    [theme.fn.smallerThan('md')]: {
      display: 'none'
    }
  },
  navLink: {
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    font: 'inherit',
    padding: 0
  },
  navLinkLabel: {
    fontSize: 18
  },
  selectItem: {
    wordBreak: 'break-word'
  }
}))

type NavItemBase = {
  label: string
  href: string
}

type NavItem = NavItemBase & {
  children?: NavItem[]
}

export interface AppLayoutProps {
  buttons?: ReactNode
  children: ReactNode
  title?: string
}

export const AppLayout: FC<AppLayoutProps> = ({ buttons, children, title }) => {
  const [showDrawer, drawerHandler] = useDisclosure(false)
  const [drawerExpanded, setDrawerExpanded] = useState<Record<string, boolean>>({})
  const { clearPreferences, profile, requestSiteChange, siteId, sites } = useSugarContext()
  // A site switch mid-save would load site B's sheet while site A's save is still
  // resolving. Read from the query client rather than published by the attendance
  // screen, so it cannot be left stale by that screen unmounting.
  const isSavingAttendance = useIsMutating({ mutationKey: updateAttendanceMutationKey }) > 0
  const siteOptions = useMemo(
    () =>
      map(sites, (site) => ({
        label: site.name,
        value: site.id
      })),
    [sites]
  )
  const router = useRouter()
  // Routed through the provider so a screen with unsaved edits can prompt
  // first. On /roster this switch never touches the router, so the
  // routeChangeStart guard in useBlockNavigation cannot see it.
  const handleSiteChange = useCallback(
    (nextSiteId: string | null) => {
      if (!nextSiteId) return
      // The URL cleanup is handed to requestSiteChange rather than run beside
      // it. A screen with unsaved edits only stages the switch and may cancel
      // it, and stripping the param here regardless would leave the URL
      // describing a site the app never moved to.
      requestSiteChange(nextSiteId, () => {
        if ('programYear' in router.query) {
          const { programYear: _programYear, ...rest } = router.query
          void router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
        }
      })
    },
    [requestSiteChange, router]
  )
  const matchesNavHref = useCallback(
    (href: string) => {
      const [path, qs] = href.split('?')

      if (router.pathname !== path) return false
      if (!qs) return true

      const params = new URLSearchParams(qs)
      let matched = true

      params.forEach((value, key) => {
        if (router.query[key] !== value) matched = false
      })

      return matched
    },
    [router.pathname, router.query]
  )
  const { classes } = useStyles()
  const navItems = useMemo<NavItem[]>(
    () => [
      {
        href: '/roster',
        label: 'Roster'
      },
      {
        href: '/attendance',
        label: 'Attendance'
      },
      {
        href: '#',
        label: 'Provider',
        children: [
          { href: '/provider-profile', label: 'Update Profile' },
          { href: '/tuition-rates', label: 'Tuition Rates' }
        ]
      },
      {
        href: '#',
        label: 'Documents',
        children: [
          { href: '/documents?tab=payment-reports', label: 'Payment Reports' },
          { href: '/documents?tab=family-letters', label: 'Letters' },
          { href: '/documents?tab=knowledge-base', label: 'Knowledge Base' }
        ]
      }
    ],
    []
  )

  const onSignOut = useCallback(async () => {
    clearPreferences()
    await signOut({
      redirect: false
    })

    drawerHandler.close()

    window.location.replace(process.env.NEXT_PUBLIC_LOGOUT_URL)
  }, [clearPreferences, drawerHandler])

  return (
    <AppShell
      header={
        <Header height={headerHeight}>
          <Container
            className={classes.header}
            size='xl'
          >
            <Link href='/dashboard'>
              <Image
                alt='logo'
                height={60}
                src={logoImg}
                priority
              />
            </Link>
            <Group
              align='center'
              className={classes.links}
              noWrap
              position='center'
              spacing='xl'
            >
              {map(navItems, (navItem, navItemIdx) =>
                navItem.children ? (
                  <Menu key={`anchor-${navItemIdx}`}>
                    <Menu.Target>
                      <Anchor
                        className={classes.navLink}
                        color='brand-dark-blue'
                        component='button'
                        size='md'
                        type='button'
                        underline={false}
                        weight='600'
                      >
                        {navItem.label}
                      </Anchor>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {map(navItem.children, (child, childIdx) => (
                        <Menu.Item
                          component={Link}
                          href={child.href}
                          key={`anchor-${navItemIdx}-${childIdx}`}
                        >
                          {child.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                ) : (
                  <Anchor
                    className={classes.navLink}
                    color='brand-dark-blue'
                    component={Link}
                    href={navItem.href}
                    key={`anchor-${navItemIdx}`}
                    size='md'
                    underline={false}
                    weight='600'
                  >
                    {navItem.label}
                  </Anchor>
                )
              )}
            </Group>
            <Group
              align='center'
              className={classes.menu}
              noWrap
              position='right'
            >
              <Select
                classNames={{
                  item: classes.selectItem
                }}
                data={siteOptions}
                disabled={isSavingAttendance}
                onChange={handleSiteChange}
                size='sm'
                width={300}
                value={siteId}
                variant='default'
              />
              <AppSelector />
              <Menu
                offset={0}
                position='bottom'
                transitionProps={{
                  transition: 'pop',
                  duration: 200
                }}
              >
                <Menu.Target>
                  <Avatar
                    color='brand-dark-blue'
                    radius='xl'
                    title={`${profile?.firstName} ${profile?.lastName}`}
                    variant='filled'
                  >
                    {profile?.id && initials(`${profile?.firstName} ${profile?.lastName}`, ' ')}
                  </Avatar>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    component={Link}
                    icon={<IconUserEdit size={16} />}
                    href='/profile'
                  >
                    My Profile
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    icon={<IconCashBanknote size={16} />}
                    href='/payment-setup'
                  >
                    Payment Setup
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconLogout size={16} />}
                    onClick={onSignOut}
                  >
                    Sign Out
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
            <Burger
              opened={showDrawer}
              onClick={drawerHandler.toggle}
              className={classes.burger}
              size='sm'
            />
          </Container>
        </Header>
      }
      padding='lg'
    >
      <Head>
        <title>{`DPP${title ? ` / ${title}` : null}`}</title>
      </Head>
      <Container
        px={0}
        size='xl'
      >
        <Group
          align='center'
          mb='lg'
          noWrap
          position='apart'
        >
          <Title
            color='white'
            mb={0}
            order={1}
          >
            {title}
          </Title>
          {buttons}
        </Group>
        {children}
      </Container>
      <Drawer
        className={classes.drawer}
        styles={{
          inner: {
            marginTop: headerHeight
          }
        }}
        opened={showDrawer}
        onClose={drawerHandler.close}
        position='right'
        size='100%'
        withCloseButton={false}
        withOverlay={false}
      >
        <Select
          data={siteOptions}
          disabled={isSavingAttendance}
          onChange={handleSiteChange}
          size='lg'
          value={siteId}
        />
        <NavLink
          classNames={{
            label: classes.navLinkLabel
          }}
          color='brand-dark-blue'
          component={Link}
          href='/dashboard'
          label='Dashboard'
          onClick={drawerHandler.close}
          variant='filled'
        />
        {map(navItems, (link, linkIdx) => {
          if (!link.children?.length) {
            return (
              <NavLink
                active={matchesNavHref(link.href)}
                classNames={{
                  label: classes.navLinkLabel
                }}
                color='brand-dark-blue'
                component={Link}
                href={link.href as string}
                key={`nav-link-${linkIdx}`}
                label={link.label}
                onClick={drawerHandler.close}
                variant='filled'
              />
            )
          }

          const autoOpened = some(link.children, (c) => matchesNavHref(c.href))
          const isOpened = drawerExpanded[link.label] ?? autoOpened

          return (
            <NavLink
              active={matchesNavHref(link.href)}
              classNames={{
                label: classes.navLinkLabel
              }}
              color='brand-dark-blue'
              key={`nav-link-${linkIdx}`}
              onClick={() => setDrawerExpanded((prev) => ({ ...prev, [link.label]: !isOpened }))}
              opened={isOpened}
              label={link.label}
              variant='filled'
            >
              {map(link.children, (child, childIdx) => (
                <NavLink
                  active={matchesNavHref(child.href)}
                  classNames={{
                    label: classes.navLinkLabel
                  }}
                  color='brand-dark-blue'
                  component={Link}
                  href={child.href as string}
                  key={`nav-link-${linkIdx}-${childIdx}`}
                  label={child.label}
                  onClick={drawerHandler.close}
                  variant='filled'
                />
              ))}
            </NavLink>
          )
        })}
        <NavLink
          classNames={{
            label: classes.navLinkLabel
          }}
          color='brand-dark-blue'
          component={Link}
          href='/profile'
          label='My Profile'
          variant='filled'
        />
        <NavLink
          classNames={{
            label: classes.navLinkLabel
          }}
          color='brand-dark-blue'
          component={Link}
          href='/payment-setup'
          label='Payment Setup'
          variant='filled'
        />
        <NavLink
          classNames={{
            label: classes.navLinkLabel
          }}
          color='brand-dark-blue'
          label='Sign Out'
          onClick={onSignOut}
          variant='filled'
        />
      </Drawer>
    </AppShell>
  )
}
